import React, { useState, useEffect } from 'react';

// Global array to hold image references in the background.
// This prevents the browser's Garbage Collector from deleting the images
// from memory, ensuring they never break or expire when the animation loops.
const memoryCache = [];

// The smart image component that handles loading and error states
const SafeImage = ({ src, alt, className }) => {
    const [status, setStatus] = useState('loading'); // 'loading', 'loaded', or 'error'

    // Reset status if the src changes
    useEffect(() => {
        setStatus('loading');
    }, [src]);

    return (
        <div className="relative w-full h-full bg-gray-100">
            {/* 1. Show a loading pulse while downloading */}
            {status === 'loading' && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            )}

            {/* 2. Show a clean fallback if the download breaks from the database */}
            {status === 'error' && (
                <div className="absolute inset-0 bg-gray-200 flex flex-col items-center justify-center text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs font-semibold">Image unavailable</span>
                </div>
            )}

            {/* 3. The actual image (Hidden until fully loaded) */}
            <img
                src={src}
                alt={alt}
                loading="lazy"
                className={`${className} transition-opacity duration-500 ${status === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setStatus('loaded')} // Fires only when 100% downloaded
                onError={() => setStatus('error')} // Fires if the connection drops
            />
        </div>
    );
};

const InspirationScroll = ({ items = [], onDesignSelect }) => {
    // Duplicate the items so the marquee can loop seamlessly
    const displayItems = items.length > 0 ? [...items, ...items] : [];

    useEffect(() => {
        // As soon as the component mounts, silently load the images into RAM
        if (items.length > 0) {
            items.forEach(item => {
                if (!item.imageUrl) return;
                const img = new Image();
                img.src = item.imageUrl;
                memoryCache.push(img);
            });
        }
    }, [items]);

    if (!items.length) {
        return null;
    }

    return (
        <section className="py-16 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
            <div className="container mx-auto text-center mb-8 sm:mb-12 px-4">
                <h2 className="text-2xl sm:text-4xl font-bold text-gray-800">From Imagination to Reality</h2>
                <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
                    See how a simple text prompt transforms into stunning detailed artwork ready for your merchandise.
                    <br />
                    <span className="text-sm font-semibold text-blue-600 mt-2 block">Click any design to customize!</span>
                </p>
            </div>

            <div className="relative w-full">
                <div className="flex animate-marquee space-x-8 px-4">
                    {displayItems.map((item, idx) => (
                        <div
                            key={`${item.id}-${idx}`}
                            onClick={() => onDesignSelect && onDesignSelect(item)}
                            className="flex-none w-64 sm:w-80 bg-white rounded-xl shadow-xl overflow-hidden flex flex-col transform hover:scale-105 transition-transform duration-300 border border-gray-100 cursor-pointer group"
                        >
                            <div className="h-64 overflow-hidden relative bg-gray-100">
                                {/* Using the new SafeImage component here */}
                                <SafeImage
                                    src={item.imageUrl}
                                    alt={item.name}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center pointer-events-none">
                                    <div className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-900 font-bold py-2 px-6 rounded-full transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 shadow-lg pointer-events-auto">
                                        Customize
                                    </div>
                                </div>
                            </div>
                            <div className="p-5 flex-1 flex items-center justify-center bg-gray-900 text-white relative">
                                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 rounded-full p-2 shadow-lg z-10">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <p className="text-sm font-mono text-center italic opacity-90 leading-relaxed pt-2">"{item.name}"</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes marquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); } 
                }
                .animate-marquee {
                    animation: marquee 180s linear infinite;
                    width: max-content;
                    /* Hardware acceleration removed to prevent blurry GPU compression */
                }
                .animate-marquee:hover {
                    animation-play-state: paused;
                }
            `}</style>
        </section>
    );
};

export default InspirationScroll;